import { animated, SpringConfig, SpringValue, to, useSpring, useSpringRef } from '@react-spring/web';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { IconButton } from 'src/common/icon-button';
import { CloseStrongIcon, UndorderedListStrongIcon } from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { BlockView } from '../block-view';
import { ACTIVE_SECTION_TOP, getActiveSectionId } from '../helpers/get-active-section-id';
import { getSuperRect } from '../helpers/get-super-rect';
import { Block, BlockRenderElement, Rect, Section } from '../types';
import { useReactSpringScrollTo } from '../utils/react-spring-scroll-to.hook';
import { usePlayerStore } from './player-store';

const DRAWER_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

const THUMBNAIL_LAYOUT = {
  width: 50,
  height: 60,
  contentPadding: 8,
};

function getHtmlText(text: string) {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.textContent || div.innerText || '';
}

type SectionCardData = {
  section: Section;
  title: Nullable<string>;
  blockRenderElements: BlockRenderElement[];
};

export const MobilePortraitNavigationBarAlt = () => {
  const {
    layout: { renderElements, layoutReady, sections, sectionRects, innerActualHeight },
    sectionDrawerOpen,
    innerAreaHeight,
    playerContentElement,
    canvasScrollPosition,
    setSectionDrawerOpen,
  } = usePlayerStore();
  const [currentSectionId, setCurrentSectionId] = React.useState<Nullable<Section['id']>>(null);
  const [scrollToSectionId, setScrollToSectionId] = useState<Section['id'] | null>(null);

  const renderElementsMap = useMemo(() => {
    const map = new Map<Block['id'], BlockRenderElement>();
    for (const re of renderElements) {
      if (re.type === 'block') {
        map.set(re.block.id, re);
      }
    }
    return map;
  }, [renderElements]);

  const sectionCardData: SectionCardData[] = sections.map((section) => {
    let title = section.name;
    if (title === null) {
      const textBlocks = section.blocks.filter((block) => block.content.type === 'text');
      for (const block of textBlocks) {
        if (block.content.type === 'text') {
          title = getHtmlText(block.content.value);
        }
        if (title) {
          break;
        }
      }
    }
    const blockRenderElements = section.blocks
      .map((block) => renderElementsMap.get(block.id))
      .filter(Boolean) as BlockRenderElement[];

    return {
      section,
      title: title,
      blockRenderElements,
    };
  });

  const updateActiveSectionId = useRefCallback(() => {
    if (!innerAreaHeight) return null;
    if (!innerActualHeight) return null;
    const scrollBasedActiveId = getActiveSectionId({
      sections,
      sectionRects,
      innerAreaHeight,
      innerActualHeight,
      canvasScrollPosition,
    });
    if (!scrollBasedActiveId) return;
    if (scrollToSectionId) return;
    if (scrollBasedActiveId === currentSectionId) return;
    console.log('DesktopSectionDrawer: update active section', scrollBasedActiveId);
    setCurrentSectionId(scrollBasedActiveId);
  }, [
    innerAreaHeight,
    innerActualHeight,
    sections,
    sectionRects,
    canvasScrollPosition,
    currentSectionId,
    scrollToSectionId,
  ]);

  useEffect(() => {
    updateActiveSectionId();
  }, [canvasScrollPosition, innerActualHeight, innerAreaHeight, updateActiveSectionId]);

  const currentSectionIndex = sections.findIndex((section) => section.id === currentSectionId);

  const renderSectionThumbnail = (section: Section, hoverRotateClasses: string) => {
    const sectionIndex = sections.findIndex((s) => s.id === section.id);
    const sectionRect = sectionRects[sectionIndex];
    const blockRenderElements = section.blocks
      .map((block) => renderElementsMap.get(block.id))
      .filter(Boolean) as BlockRenderElement[];
    const availableWidth = THUMBNAIL_LAYOUT.width - 2 * THUMBNAIL_LAYOUT.contentPadding;
    const scale = availableWidth / Math.max(sectionRect.width, 1);

    return (
      <div
        className={classNames(
          'bg-white rounded shrink-0 relative overflow-hidden transition origin-bottom-right',
          hoverRotateClasses
        )}
        style={{
          width: `${THUMBNAIL_LAYOUT.width}px`,
          height: `${THUMBNAIL_LAYOUT.height}px`,
        }}
      >
        <div
          className='absolute rounded overflow-hidden'
          style={{
            left: `${THUMBNAIL_LAYOUT.contentPadding}px`,
            top: `${THUMBNAIL_LAYOUT.contentPadding}px`,
            right: `${THUMBNAIL_LAYOUT.contentPadding}px`,
            bottom: `${THUMBNAIL_LAYOUT.contentPadding}px`,
          }}
        >
          <div
            className='absolute origin-top-left'
            style={{
              transform: `scale(${scale})`,
              width: `${sectionRect.width}px`,
              height: `${sectionRect.height}px`,
              left: `${0}px`,
              top: `${0}px`,
            }}
          >
            <div className='absolute w-full h-full origin-center'>
              {blockRenderElements.map((re) => (
                <BlockView
                  key={re.block.id}
                  block={re.block}
                  rect={re.rect}
                  style={{
                    x: new SpringValue(re.rect.x - sectionRect.x),
                    y: new SpringValue(re.rect.y - sectionRect.y),
                    width: new SpringValue(re.rect.width),
                    height: new SpringValue(re.rect.height),
                    opacity: new SpringValue(1),
                    scale: new SpringValue(1),
                  }}
                  grabbing={false}
                  contextMenuOpen={false}
                  hasFocus={false}
                  selected={false}
                  onBlockSize={() => {}}
                  onBlur={() => {}}
                  onFocus={() => {}}
                  onDrag={() => {}}
                  onReplaceBlock={() => {}}
                  onSetVerticalAlignment={() => {}}
                  onStart={() => {}}
                  onStop={() => {}}
                  onUpdateBlockContent={() => {}}
                  selectionCoords={null}
                  setContextMenuState={() => {}}
                />
              ))}
            </div>
          </div>
        </div>
        <div className='absolute inset-0 bg-transparent' />
      </div>
    );
  };

  let firstThumbnailIndex = -1;
  let secondThumbnailIndex = -1;
  let thirdThumbnailIndex = -1;
  if (currentSectionIndex !== -1 && sectionCardData.length > 0) {
    if (sectionCardData.length === 1) {
      firstThumbnailIndex = 0;
    } else if (sectionCardData.length === 2) {
      if (currentSectionIndex === 0) {
        firstThumbnailIndex = 0;
        secondThumbnailIndex = 1;
      } else {
        firstThumbnailIndex = 1;
        secondThumbnailIndex = 0;
      }
    } else {
      if (currentSectionIndex === 0) {
        firstThumbnailIndex = 0;
        secondThumbnailIndex = firstThumbnailIndex + 1;
        thirdThumbnailIndex = firstThumbnailIndex + 2;
      } else if (currentSectionIndex === sectionCardData.length - 1) {
        firstThumbnailIndex = currentSectionIndex;
        secondThumbnailIndex = firstThumbnailIndex - 1;
        thirdThumbnailIndex = firstThumbnailIndex - 2;
      } else {
        firstThumbnailIndex = currentSectionIndex;
        secondThumbnailIndex = firstThumbnailIndex - 1;
        thirdThumbnailIndex = firstThumbnailIndex + 1;
      }
    }
  }

  const containerRef = useRef<HTMLDivElement>(null);
  useClickAway(containerRef, () => {
    setSectionDrawerOpen(false);
  });

  const drawerStyles = useSpring({
    drawerOpacity: sectionDrawerOpen ? 1 : 0,
    menuButtonOpacity: sectionDrawerOpen ? 0 : 1,
    y: sectionDrawerOpen ? 0 : 24,
    config: DRAWER_ANIMATION_CONFIG,
    // config: { duration: 2000 },
  });

  const { scrollTo } = useReactSpringScrollTo();

  useEffect(() => {
    if (scrollToSectionId && playerContentElement) {
      const sectionIndex = sections.findIndex((s) => s.id === currentSectionId);
      if (sectionIndex === -1) {
        return;
      }
      const sectionRect = sectionRects[sectionIndex];
      if (!sectionRect) {
        return;
      }
      let scrollY = sectionRect.y - ACTIVE_SECTION_TOP;
      scrollY = Math.max(scrollY, 0);
      scrollTo(playerContentElement, scrollY, () => {
        setScrollToSectionId(null);
      });
    }
  }, [scrollToSectionId]);

  return (
    <>
      <animated.div
        className={classNames('absolute inset-0 flex flex-col items-center justify-end', {
          'opacity-0': !layoutReady,
        })}
        style={{
          opacity: drawerStyles.drawerOpacity,
          transform: drawerStyles.y.to((y) => `translateY(${y}px)`),
        }}
      >
        <div
          ref={containerRef}
          className={classNames('flex flex-col gap-2 bg-neue-glass neue-glass-backdrop-blur rounded-t-lg', {
            'pointer-events-auto': sectionDrawerOpen,
          })}
          style={{
            maxHeight: 'calc(100vh - 50px)',
          }}
        >
          <animated.div className='flex flex-col gap-1 my-2 px-2 overflow-y-auto text-white'>
            {sectionCardData.map(({ section, title, blockRenderElements }, i) => {
              return (
                <div
                  className={classNames(
                    'flex flex-1 items-center gap-2 transition-opacity cursor-pointer hover:opacity-100',
                    {
                      'opacity-70': i !== currentSectionIndex,
                      'opacity-100': i === currentSectionIndex,
                    }
                  )}
                  onClick={() => {
                    console.log('DesktopSectionDrawer: onClick: section.id: ', section.id);
                    setCurrentSectionId(section.id);
                    setScrollToSectionId(section.id);
                  }}
                >
                  {renderSectionThumbnail(section, '')}
                  <div className='flex flex-1 text-bedrock-mobile-input items-center line-clamp-2'>{title}</div>
                </div>
              );
            })}
          </animated.div>
          <div
            className='group self-center flex items-center justify-center p-2.5 cursor-pointer'
            onClick={() => {
              console.log('DesktopSectionDrawer: onClick: close');
              return setSectionDrawerOpen(false);
            }}
          >
            <CloseStrongIcon className='text-white group-hover:text-white/70 transition' />
          </div>
        </div>
      </animated.div>

      <animated.div
        className={classNames(
          'absolute left-0 bottom-0 w-full h-20 pointer-events-none flex items-end justify-center pb-[14px]'
        )}
        style={{
          opacity: drawerStyles.menuButtonOpacity,
          background: 'linear-gradient(360deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 100%)',
          backgroundSize: '100% 100%',
        }}
      >
        <div
          className={classNames('flex items-center justify-center gap-1.5', {
            'pointer-events-auto': !sectionDrawerOpen,
          })}
          onClick={() => setSectionDrawerOpen(true)}
        >
          <svg
            className='text-neue-gray-0'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M8.40001 5.59995C8.40001 5.70604 8.35786 5.80778 8.28285 5.88279C8.20783 5.95781 8.10609 5.99995 8.00001 5.99995C7.89392 5.99995 7.79218 5.95781 7.71716 5.88279C7.64215 5.80778 7.60001 5.70604 7.60001 5.59995C7.60001 5.49386 7.64215 5.39212 7.71716 5.31711C7.79218 5.24209 7.89392 5.19995 8.00001 5.19995C8.10609 5.19995 8.20783 5.24209 8.28285 5.31711C8.35786 5.39212 8.40001 5.49386 8.40001 5.59995Z'
              stroke='#101010'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M3 9.39995L2.3712 12.8567C2.35022 12.9721 2.35483 13.0906 2.38471 13.2039C2.41458 13.3172 2.46899 13.4226 2.54408 13.5126C2.61918 13.6025 2.71312 13.6749 2.81928 13.7246C2.92543 13.7742 3.0412 13.8 3.1584 13.7999H12.8416C12.9588 13.8 13.0746 13.7742 13.1807 13.7246C13.2869 13.6749 13.3808 13.6025 13.4559 13.5126C13.531 13.4226 13.5854 13.3172 13.6153 13.2039C13.6452 13.0906 13.6498 12.9721 13.6288 12.8567L13 9.39995M11.4 5.55995C11.4 7.99995 8 10.6 8 10.6C8 10.6 4.6 7.99995 4.6 5.55995C4.6 3.70395 6.1224 2.19995 8 2.19995C9.8776 2.19995 11.4 3.70395 11.4 5.55995Z'
              stroke='#101010'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
          <div className='text-[14px] leading-5 antialiased font-medium text-neue-gray-0'>Journey Map</div>
        </div>
      </animated.div>
    </>
  );
};
